/** Turn accidental single-letter snake (c_m_s, t_j_c) into cms / tjc. */
function collapseAcronymSnake(slug: string): string {
  const parts = slug.split("_");
  if (parts.length < 2) return slug;
  if (parts.every((p) => p.length === 1)) return parts.join("");
  return slug;
}

function camelSegmentToSnake(segment: string): string {
  if (!segment) return "";
  const step = segment
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2");
  return step.toLowerCase().replace(/^_+|_+$/g, "").replace(/_+/g, "_");
}

/**
 * Normalize accrediting body slug for GET /api/v1/accreditations multi-body calls.
 * Mirrors server `normalize_accrediting_body_slug` (snake_case, acronyms, camelCase).
 */
export function normalizeAccreditingBodySlug(slug: string): string {
  const s = (slug ?? "").trim();
  if (!s) return "";
  const condensed = s.replace(/_+/g, "_");
  if (/^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(condensed)) {
    return collapseAcronymSnake(condensed);
  }
  if (s === s.toUpperCase() && /[A-Z]/.test(s)) {
    return collapseAcronymSnake(s.toLowerCase());
  }
  if (s.includes("_")) {
    return s
      .split("_")
      .filter(Boolean)
      .map((p) => camelSegmentToSnake(p))
      .join("_");
  }
  return collapseAcronymSnake(camelSegmentToSnake(s));
}

/**
 * Profile multiselect often stores accrediting bodies as slug strings; dedicated
 * `accrediting_bodies` fields use `{ slug, name }[]`. Normalise so facility state
 * matches the rest of the app (e.g. Potential Penalties / Penalty.tsx signatures).
 */
export function normaliseAccreditingBodyForFacility(
  raw: unknown
): { slug: string; name: string }[] | null {
  if (raw === null) return null;
  if (!Array.isArray(raw)) return null;
  const out: { slug: string; name: string }[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      const s = item.trim();
      if (s) out.push({ slug: s, name: s });
    } else if (
      item &&
      typeof item === "object" &&
      typeof (item as { slug?: unknown }).slug === "string"
    ) {
      const slug = (item as { slug: string }).slug.trim();
      if (!slug) continue;
      const nm = (item as { name?: unknown }).name;
      const name =
        typeof nm === "string" && nm.trim() ? nm.trim() : slug;
      out.push({ slug, name });
    }
  }
  return out;
}

/** Resolve accrediting value from profile save payload (schema field_key varies). */
export function pickAccreditingBodyRawFromProfileSave(
  fields: Record<string, unknown>
): unknown | undefined {
  if (fields.accrediting_body !== undefined) return fields.accrediting_body;
  if (fields.accrediting_bodies !== undefined) return fields.accrediting_bodies;
  for (const [key, val] of Object.entries(fields)) {
    if (!(Array.isArray(val) || val === null)) continue;
    const k = key.toLowerCase();
    if (
      (k.includes("accredit") &&
        (k.includes("body") || k.includes("bodies") || k.includes("org"))) ||
      (k.includes("deem") && k.includes("authorit"))
    ) {
      return val;
    }
  }
  return undefined;
}
